import { z } from 'zod';

import { Acquirer } from 'data/merchant/models/acquirer';

export const DepositStatus = z.enum([
  'CREATED',
  'MONEY_RECEIVED',
  'PAID',
  'EXPIRED',
  'DISPUTED',
  'CANCELLED',
]);
export type DepositStatus = z.infer<typeof DepositStatus>;

export const DepositTransferType = z.enum(['CARD', 'SBP', 'QR']);
export type DepositTransferType = z.infer<typeof DepositTransferType>;

export const DepositCredentialsType = z.enum([
  ...DepositTransferType.options,
  'SBP_OR_CARD',
]);
export type DepositCredentialsType = z.infer<typeof DepositCredentialsType>;

export const DepositCredentials = z.object({
  type: DepositTransferType,
  value: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  cardNumber: z.string().nullish(),
  qr: z.string().nullish(),
  bank: z.string(),
  name: z.string().optional(),
});
export type DepositCredentials = z.infer<typeof DepositCredentials>;

export const DepositMethod = z.enum([
  'SBP',
  'SBP_VIP',
  'SBP_CLASSIC',
  'SBP_100',
  'SBP_GU',
  'SBP_CA',
  'CARD',
  'CARD_VIP',
  'CARD_CLASSIC',
  'CARD_GU',
  'CARD_CA',
  'SBER_AZN',
  'MNT_CARD',
  'UZS_CARD',
  'UZS_CARD_2',
  'AZN_CARD',
  'AMD_TG_SBP',
  'AMD_TG_CARD',
  'AMD_TG',
  'TJS_TG',
  'UZS_TG',
  'MOBCOM',
  'VND_TG_QR',
  'PHP_TG_QR',
  'VND_TG_FTD',
  'VND_TG_VTB',
  'VND_TG_MTS',
  'VND_TG_GAZPROM',
  'SBP_IDP',
  'CARD_IDP',
  'SBER_QR',
  'ALFA_QR',
  'CARD_MONO_YA',
  'SBP_MONO_YA',
  'CARD_MONO_OZ',
  'SBP_MONO_OZ',
]);
export type DepositMethod = z.infer<typeof DepositMethod>;

export const nameRequiredForMethod = (method: DepositMethod) =>
  ['TJS_TG', 'UZS_TG', 'AMD_TG'].includes(method);

export const isSbpMethod = (method: DepositMethod) =>
  [
    'SBP',
    'SBP_VIP',
    'SBP_100',
    'AMD_TG_SBP',
    'MOBCOM',
    'SBP_GU',
    'SBP_CA',
    'SBP_MONO_YA',
    'SBP_MONO_OZ',
  ].includes(method);

export const isCardMethod = (method?: DepositMethod) =>
  method &&
  [
    'CARD',
    'CARD_CA',
    'CARD_VIP',
    'MNT_CARD',
    'UZS_CARD',
    'UZS_CARD_2',
    'AZN_CARD',
    'AMD_TG_CARD',
    'CARD_MONO_YA',
    'CARD_MONO_OZ',
  ].includes(method);

export const isQRMethod = (method: DepositMethod) =>
  ['VND_TG_QR', 'PHP_TG_QR', 'SBER_QR', 'ALFA_QR'].includes(method);

export const isRuMethod = (method?: DepositMethod) =>
  method &&
  [
    'CARD',
    'SBP',
    'SBP_GU',
    'CARD_GU',
    'CARD_VIP',
    'SBP_VIP',
    'SBP_100',
    'AMD_TG_SBP',
    'AMD_TG_CARD',
    'VND_TG_QR',
    'VND_TG_PHP',
    'AMD_TG',
    'TJS_TG',
    'UZS_TG',
    'MOBCOM',
    'SBER_QR',
    'ALFA_QR',
    'CARD_MONO_YA',
    'SBP_MONO_YA',
    'CARD_MONO_OZ',
    'SBP_MONO_OZ',
  ].includes(method);

export const isDeeplinkMethod = (method?: DepositMethod) =>
  method &&
  [
    'AMD_TG_SBP',
    'AMD_TG_CARD',
    'AMD_TG',
    'TJS_TG',
    'UZS_TG',
    'MOBCOM',
    'VND_TG_QR',
    'SBER_QR',
    'ALFA_QR',
  ].includes(method);

export const isTraingleMethod = (method: DepositMethod) =>
  ['SBP', 'CARD', 'SBP_VIP', 'CARD_VIP'].includes(method);

export const Deposit = z.object({
  id: z.string(),
  paymentLink: z.string().nullish(),
  acquirer: Acquirer,
  method: DepositMethod,
  fromBank: z.string().nullable(),
  credentials: DepositCredentials.nullish(),
  amount: z.string().nullable(),
  currency: z.string(),
  expiryDate: z.coerce.date(),
  successRedirect: z.string().nullable(),
  failureRedirect: z.string().nullable(),
  customerId: z.string().nullish(),
  status: DepositStatus,
  crossborderAmount: z.string().nullish(),
  crossborderCurrency: z.string().nullish(),
  showChatwoot: z.boolean().nullish(),
});
export type Deposit = z.infer<typeof Deposit>;

export const DepositBank = z.enum([
  'Сбербанк',
  'ВТБ',
  'Т-Банк',
  'МТС Банк',
  'Газпромбанк',
  'Альфа-Банк',
]);
export type DepositBank = z.infer<typeof DepositBank>;

export const banksByMethod: Partial<Record<DepositMethod, DepositBank[]>> = {
  AMD_TG_CARD: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['Т-Банк'],
  ],
  AMD_TG_SBP: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values['Т-Банк'],
    DepositBank.Values.ВТБ,
  ],
  TJS_TG: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['Т-Банк'],
    DepositBank.Values['Альфа-Банк'],
  ],
  UZS_TG: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['Т-Банк'],
  ],
  AMD_TG: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['Т-Банк'],
  ],
  MOBCOM: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['Т-Банк'],
  ],
  VND_TG_QR: [
    DepositBank.Values.Сбербанк,
    DepositBank.Values.ВТБ,
    DepositBank.Values['МТС Банк'],
    DepositBank.Values['Газпромбанк'],
  ],
  PHP_TG_QR: [DepositBank.Values.Сбербанк],

  SBP: DepositBank.options,
  CARD: DepositBank.options,
  SBP_100: DepositBank.options,
  CARD_CLASSIC: DepositBank.options,
  SBP_CLASSIC: DepositBank.options,
  SBER_QR: [DepositBank.Values.Сбербанк],
  ALFA_QR: [DepositBank.Values['Альфа-Банк']],
};

export const CountryCode = z.enum(['AM', 'RU', 'VN', 'TJ', 'UZ', 'PH']);
export type CountryCode = z.infer<typeof CountryCode>;

export const countryCodeByMethod: Partial<Record<DepositMethod, CountryCode>> =
  {
    AMD_TG_CARD: CountryCode.Values.AM,
    AMD_TG_SBP: CountryCode.Values.AM,
    SBP: CountryCode.Values.RU,
    SBP_CLASSIC: CountryCode.Values.RU,
    SBP_100: CountryCode.Values.RU,
    CARD: CountryCode.Values.RU,
    CARD_CLASSIC: CountryCode.Values.RU,
    VND_TG_QR: CountryCode.Values.VN,
    PHP_TG_QR: CountryCode.Values.PH,
    TJS_TG: CountryCode.Values.TJ,
    UZS_TG: CountryCode.Values.UZ,
  };
